import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { ESC, TRAP, useEscape, useFocusTrap } from '@/lib/keys';
import { usePresence } from '@/lib/hooks';
import { Icon } from './Icon';

// A sheet slides up over the current view: records and the admin console open in one.

export function Sheet({
  open,
  onClose,
  label,
  closeLabel = 'Close',
  kind,
  full,
  id,
  children,
  bodyRef,
}: {
  open: boolean;
  onClose: () => void;
  /** Read out by screen readers, such as "Person record". */
  label: string;
  closeLabel?: string;
  kind: 'record' | 'admin';
  /** Uses almost the whole height of the window. */
  full?: boolean;
  id?: string;
  children: ReactNode;
  bodyRef?: RefObject<HTMLDivElement | null>;
}) {
  const { mounted, shown, closing } = usePresence(open, 300);
  const ref = useRef<HTMLElement>(null);

  useEscape(open, kind === 'admin' ? ESC.admin : ESC.record, () => {
    onClose();
  });
  useFocusTrap(ref, open, kind === 'admin' ? TRAP.admin : TRAP.record);

  const returnTo = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) {
      // Give focus back to whatever opened the sheet, unless focus has already moved on somewhere else.
      const back = returnTo.current;
      returnTo.current = null;
      const active = document.activeElement;
      if (back?.isConnected && (!active || active === document.body || ref.current?.contains(active))) back.focus({ preventScroll: true });
      return;
    }
    const active = document.activeElement;
    if (!returnTo.current && active instanceof HTMLElement && active !== document.body && !ref.current?.contains(active)) returnTo.current = active;
    const t = setTimeout(() => {
      const sheet = ref.current;
      if (sheet && !sheet.contains(document.activeElement)) sheet.focus({ preventScroll: true });
    }, 60);
    return () => clearTimeout(t);
  }, [open]);

  if (!mounted) return null;
  return (
    <div
      id={id}
      className={['sheet-layer', kind === 'record' ? 'rec' : '', 'open', shown && open ? 'show' : '', closing ? 'closing' : ''].filter(Boolean).join(' ')}
      aria-hidden={open ? undefined : true}
    >
      <div className="sheet-scrim" onMouseDown={onClose} />
      <section ref={ref} className={full ? 'sheet full' : 'sheet'} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1}>
        <span className="grab" aria-hidden="true" onClick={onClose} />
        <button type="button" className="icon-btn sheet-x" aria-label={closeLabel} data-tip="Close" data-key="Esc" onClick={onClose}>
          <Icon name="x" />
        </button>
        <div className="sheet-body" ref={bodyRef}>
          {children}
        </div>
      </section>
    </div>
  );
}
